import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const services = [
  {
    icon: "ri-code-s-slash-line",
    title: "Custom Software Development",
    desc: "We design and build bespoke software solutions tailored to your business needs — web apps, enterprise platforms, APIs, integrations, and everything in between.",
    tags: ["Web Apps", "Enterprise", "APIs", "Integrations"],
  },
  {
    icon: "ri-smartphone-line",
    title: "Mobile App Development",
    desc: "Native and cross-platform mobile applications for iOS and Android. From MVP to a fully-featured product, we build mobile experiences users love.",
    tags: ["iOS", "Android", "Cross-platform", "React Native"],
  },
  {
    icon: "ri-pencil-ruler-line",
    title: "UI/UX Design",
    desc: "User-centred design that balances aesthetics with functionality. We create wireframes, prototypes, and full design systems that make your product intuitive and beautiful.",
    tags: ["Wireframing", "Prototyping", "Design Systems", "User Research"],
  },
  {
    icon: "ri-lightbulb-line",
    title: "IT Consulting",
    desc: "Strategic technology advice to help you make informed decisions. We assess your current infrastructure, identify opportunities, and build a technology roadmap aligned with your goals.",
    tags: ["Tech Audit", "Roadmapping", "Architecture", "Process Optimisation"],
  },
  {
    icon: "ri-bar-chart-line",
    title: "Digital Marketing",
    desc: "Data-driven marketing strategies that attract, engage, and convert your target audience. We manage the full funnel — from awareness to retention.",
    tags: ["SEO", "Paid Ads", "Social Media", "Content Strategy"],
  },
  {
    icon: "ri-shield-check-line",
    title: "QA & Testing",
    desc: "Comprehensive quality assurance to ensure your product works flawlessly. Manual and automated testing, performance analysis, and security audits.",
    tags: ["Manual Testing", "Automation", "Performance", "Security"],
  },
];

const process = [
  { step: "01", title: "Discovery & Scoping", desc: "We start by deeply understanding your goals, audience, and constraints. This shapes the project scope and sets a clear direction." },
  { step: "02", title: "Strategy & Planning", desc: "We define the architecture, choose the right technology stack, and create a detailed project roadmap with milestones." },
  { step: "03", title: "Design & Prototyping", desc: "Our designers create wireframes and high-fidelity prototypes. You review and approve before a single line of code is written." },
  { step: "04", title: "Development & Testing", desc: "Our engineers build the product in sprints with continuous testing. You get regular demos so nothing surprises you at the end." },
  { step: "05", title: "Launch & Go-to-Market", desc: "We deploy your product and support the launch — including marketing strategy, app store optimisation, and initial growth campaigns." },
  { step: "06", title: "Support & Growth", desc: "Post-launch, we monitor performance, fix issues, and continuously improve your product based on real user data." },
];

export default function OurServices() {
  return (
    <div className="min-h-screen font-sans" style={{ backgroundColor: "#FEF7EB" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 px-6 md:px-16 overflow-hidden">
        <div
          className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full opacity-25 pointer-events-none"
          style={{ backgroundColor: "#D4F419", filter: "blur(100px)" }}
        />
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: "#0073E5" }}>
            Our Services
          </p>
          <h1 className="text-5xl md:text-7xl font-bold text-black leading-tight max-w-3xl mb-8">
            Everything you need<br />to build and grow.
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
            From concept to code to conversion — we offer a full spectrum of IT and digital marketing services
            so you never need to juggle multiple vendors again.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="px-6 md:px-16 pb-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <div key={s.title} className="bg-white rounded-2xl p-7 flex flex-col">
              <div className="w-11 h-11 flex items-center justify-center rounded-full mb-5 flex-shrink-0" style={{ backgroundColor: "#D4F419" }}>
                <i className={`${s.icon} text-black text-lg`}></i>
              </div>
              <h3 className="font-bold text-black text-lg mb-3">{s.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed flex-1 mb-5">{s.desc}</p>
              <div className="flex flex-wrap gap-2">
                {s.tags.map((tag) => (
                  <span key={tag} className="text-xs font-medium px-3 py-1 rounded-full" style={{ backgroundColor: "#FEF7EB", color: "#0073E5" }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="px-6 md:px-16 py-24 rounded-3xl mx-4 md:mx-8 mb-16" style={{ backgroundColor: "#0073E5" }}>
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-widest mb-4 text-white/60">
            How We Work
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-12 max-w-xl">
            Our process, step by step
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {process.map((p) => (
              <div key={p.step} className="rounded-2xl p-6" style={{ backgroundColor: "rgba(255,255,255,0.1)" }}>
                <span className="text-4xl font-bold opacity-30 text-white block mb-3">{p.step}</span>
                <h3 className="text-white font-semibold text-base mb-2">{p.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-16 pb-24">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">Ready to get started?</h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto mb-8">
            Tell us about your project and we&apos;ll get back to you within 24 hours with a plan.
          </p>
          <Link
            to="/contact-us"
            className="inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap text-sm"
            style={{ backgroundColor: "#0073E5" }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#005bbf")}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#0073E5")}
          >
            Start a project
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
